import { useEffect, useId, useRef, useState } from 'react';

export interface InfoTipProps {
  /** Der Begriff, der erklaert wird — steht als Ueberschrift im Feld. */
  term: string;
  /** Was es ist, in Alltagssprache. */
  what: string;
  /** Warum es zaehlt, mit spuerbarer Folge. */
  why: string;
  /** Was zu tun ist. */
  todo: string;
}

/**
 * Erklärung eines Fachbegriffs.
 *
 * Öffnet auf Klick und auf Tastaturfokus — **nicht** beim blossen Zeigen mit
 * der Maus. Ein Erklaertext, der nur beim Ueberfahren erscheint, ist auf
 * jedem Telefon unerreichbar, und genau dort sitzt der Nutzer, der ihn am
 * dringendsten braucht.
 *
 * Die drei Textteile sind einzeln vorgeschrieben statt als freier Fliesstext.
 * Das ist Absicht: es zwingt jeden, der einen Begriff ergaenzt, den Bau
 * einzuhalten — was es ist, warum es zaehlt, was zu tun ist. Ein freies Feld
 * fuellt sich nach zwei Wochen mit Fachsprache.
 */
export function InfoTip({ term, what, why, todo }: InfoTipProps) {
  const [offen, setOffen] = useState(false);
  const id = useId();
  const wurzel = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!offen) return;

    function beiTaste(event: KeyboardEvent) {
      if (event.key === 'Escape') setOffen(false);
    }
    function beiKlick(event: MouseEvent) {
      if (!wurzel.current?.contains(event.target as Node)) setOffen(false);
    }

    document.addEventListener('keydown', beiTaste);
    document.addEventListener('mousedown', beiKlick);
    return () => {
      document.removeEventListener('keydown', beiTaste);
      document.removeEventListener('mousedown', beiKlick);
    };
  }, [offen]);

  return (
    <span className="infotip" ref={wurzel}>
      <button
        type="button"
        className="infotip__button"
        aria-expanded={offen}
        aria-controls={id}
        onClick={() => setOffen((zuvor) => !zuvor)}
        /* Die Beschriftung nennt den Begriff. Ein blosses "Info" liest der
           Screenreader an dreissig Stellen gleich vor. */
        aria-label={`Was bedeutet ${term}?`}
      >
        <span aria-hidden="true">i</span>
      </button>

      {offen && (
        <span className="infotip__panel" id={id} role="note">
          <span className="infotip__term">{term}</span>
          <span className="infotip__line">{what}</span>
          <span className="infotip__line">{why}</span>
          <span className="infotip__line infotip__todo">{todo}</span>
        </span>
      )}
    </span>
  );
}
